package org.matthewo.plots

import emotion.react.css
import js.coroutines.awaitCancellation
import js.objects.unsafeJso
import org.matthewo.collections.forEachReverse
import org.matthewo.js.toFixed
import org.matthewo.recharts.cartesian.ReferenceLine
import org.matthewo.recharts.cartesian.Scatter
import org.matthewo.recharts.cartesian.XAxis
import org.matthewo.recharts.cartesian.YAxis
import org.matthewo.recharts.chart.ScatterChart
import org.matthewo.recharts.collection.rangeByOrNull
import org.matthewo.recharts.component.Label
import org.matthewo.recharts.component.LabelList
import org.matthewo.recharts.component.Tooltip
import org.matthewo.recharts.container.ResponsiveContainer
import org.matthewo.recharts.type.*
import org.matthewo.styles.BACKGROUND_COLOR
import org.matthewo.styles.TITLE_FONT_COLOR
import org.matthewo.styles.lighten
import react.*
import react.dom.events.PointerType
import react.dom.html.ReactHTML.b
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.p
import web.cssom.*
import web.dom.document
import web.events.*
import web.html.HTMLInputElement
import web.keyboard.KeyboardEvent
import web.window.Window
import web.window.window
import kotlin.math.*

private const val TICK_SPACING = 10
private const val LEVEL_LINE_SPACING = 20

private const val DECIMATION_THRESHOLD = 1_000
private const val DECIMATION_SEPARATION = 0.005

fun <T> ComparisonScatterPlot() = FC<ComparisonScatterPlotProps<T>>("ComparisonScatterPlot") { props ->
    var graphWidth by useState(1.0)
    var graphHeight by useState(1.0)

    var touch by useState(false)

    var lookup by useState<Int>()

    val sorted = useMemo(props.data, props.xMetric, props.yMetric) {
        props.data.map { Triple(it, props.xMetric(it), props.yMetric(it)) }.sortedBy { it.third }
    }
    val highlight = props.data.firstOrNull { props.lookup(it) == lookup }
    val (domainX, domainY, filtered) = useMemo(sorted, props.xMetric, props.yMetric, highlight) {

        val xRange = sorted.rangeByOrNull { it.second }
        val yRange = sorted.rangeByOrNull { it.third }

        val dx = domain(xRange)
        val dy = domain(yRange)

        val dxSize = dx.last - dx.first
        val dySize = dy.last - dy.first

        val highlightPosition = highlight?.let { Triple(it, props.xMetric(it), props.yMetric(it)) }

        val display = if (sorted.size > DECIMATION_THRESHOLD) {
            val filtered = mutableListOf<Triple<T, Double, Double>>()
            sorted.forEachReverse { instance ->
                if (instance.first == highlightPosition?.first) {
                    filtered.add(instance)
                    return@forEachReverse
                }
                if (null != highlightPosition) {
                    val deltaX = (highlightPosition.second - instance.second) / dxSize
                    val deltaY = (highlightPosition.third - instance.third) / dySize
                    if (hypot(deltaX, deltaY) < DECIMATION_SEPARATION) {
                        return@forEachReverse
                    }
                }
                for (i in filtered.indices.reversed()) {
                    val deltaY = (filtered[i].third - instance.third) / dySize
                    if (deltaY > DECIMATION_SEPARATION) break
                    val deltaX = (filtered[i].second - instance.second) / dxSize
                    if (hypot(deltaX, deltaY) < DECIMATION_SEPARATION) {
                        return@forEachReverse
                    }
                }
                filtered.add(instance)
            }

            filtered.reverse()

            filtered
        } else {
            sorted
        }

        Triple(dx, dy, display)
    }
    val instances = useMemo(filtered) { filtered.toTypedArray() }

    val ComparisonScatterTooltip = FC("ComparisonScatterTooltip") { propsTooltip: dynamic ->
        if (!propsTooltip["active"].unsafeCast<Boolean>()) return@FC
        val instance = propsTooltip["payload"][0]["payload"].unsafeCast<Triple<T, Double, Double>>().first
        div {
            css {
                opacity = number(0.9)
                background = BACKGROUND_COLOR
                border = Border(2.px, LineStyle.solid, TITLE_FONT_COLOR)
                borderRadius = 4.px
                color = NamedColor.white
                padding = array(0.0.em, 0.5.em)

                if (touch) pointerEvents = Auto.auto

                ".label" {
                    fontWeight = FontWeight.bold
                }
            }
            onPointerDown = { event ->
                if (touch) props.onClick(instance)
            }
            p {
                className = ClassName("label")
                b {
                    +props.tooltipLabel(instance)
                }
            }
            val x = props.xMetric(instance)
            val y = props.yMetric(instance)
            if (null != props.sumMetricTitle) {
                p {
                    className = ClassName("intro")
                    +"${props.sumMetricTitle}: ${(x + y).toFixed(1)}"
                }
            }
            p {
                className = ClassName("intro")
                +"${props.xMetricTitle}: ${x.toFixed(1)}"
            }
            p {
                className = ClassName("intro")
                +"${props.yMetricTitle}: ${y.toFixed(1)}"
            }
        }
    }

    div {
        css {
            width = 100.pct
            height = 100.pct
            fontSize = 1.rem
            overflow = Overflow.hidden
        }
        onPointerDown = { event -> touch = PointerType.mouse != event.pointerType }
        ResponsiveContainer {
            onResize = { w, h ->
                graphWidth = w
                graphHeight = h
            }
            val shouldShowLabel = useMemo(domainX, domainX, graphWidth, graphHeight, lookup) {
                val middleX = (domainX.first + domainX.last) / 2.0
                val middleY = (domainY.first + domainY.last) / 2.0

                val middle = middleX + middleY

                val sizeX = domainX.last - domainX.first
                val spacingX = (6000.0 / graphWidth) * (sizeX / 100.0)

                val sizeY = domainY.last - domainY.first
                val spacingY = (4000.0 / graphHeight) * (sizeY / 100.0)

                val label = mutableMapOf<T, Boolean>().withDefault { false }

                if (null != lookup) {
                    if (null != highlight) label[highlight] = true
                } else {
                    for (i in filtered.lastIndex downTo 0) {
                        val (instance, x, y) = filtered[i]
                        if (!props.onlyShowTopLabels || x + y > middle) {
                            var show = true
                            for (j in (i + 1)..<filtered.size) {
                                if (y + spacingY < filtered[j].third) {
                                    break
                                }
                                if (abs(x - filtered[j].second) < spacingX * if (label[filtered[j].first] == true) 1.0 else 0.6) {
                                    show = false
                                    break
                                }
                            }
                            if (show) {
                                label[instance] = true
                            }
                        }
                    }
                }

                label
            }


            width = 100.pct
            height = 100.pct
            ScatterChart {
                css {
                    ".recharts-scatter .recharts-scatter-symbol .recharts-symbols" {
                        transition = Transition(CustomPropertyName<Color>("strokeWidth").unsafeCast<TransitionProperty>(), 500.ms, 0.ms)
                        this[CustomPropertyName("fillOpacity")] = "0.7"
                        this[CustomPropertyName("stroke")] = TITLE_FONT_COLOR
                        this[CustomPropertyName("strokeWidth")] = 1.px
                    }

                    ".recharts-scatter .recharts-scatter-symbol .recharts-symbols:hover" {
                        filter = dropShadow(0.px, 0.px, 5.px, Color("#7e23af"))
                        this[CustomPropertyName("strokeWidth")] = 10.px
                    }
                }

                margin = ChartMargin(15, 25, 35, 15)
                XAxis {
                    Label {
                        value = props.xMetricTitle
                        position = ComponentPosition.bottom
                        onClick = { props.onToggle() }
                    }
                    name = props.xMetricTitle
                    type = AxisType.number
                    dataKey = Triple<T, Double, Double>::second
                    domain = [domainX.first, domainX.last]
                    ticks = (domainX step TICK_SPACING).toList().toTypedArray()
                    allowDecimals = false
                }
                YAxis {
                    Label {
                        value = props.yMetricTitle
                        position = ComponentPosition.insideLeft
                        angle = -90
                        style = unsafeJso {
                            textAnchor = "middle"
                        }
                        onClick = { props.onToggle() }
                    }
                    name = props.yMetricTitle
                    type = AxisType.number
                    dataKey = Triple<T, Double, Double>::third
                    domain = [domainY.first, domainY.last]
                    ticks = (domainY step TICK_SPACING).toList().toTypedArray()
                    allowDecimals = false
                }
                val startSum = domainX.first + domainY.first
                val endSum = domainX.last + domainY.last
                var start = startSum.ceilTo(LEVEL_LINE_SPACING)
                var end = endSum.floorTo(LEVEL_LINE_SPACING)
                if (start == startSum) start += LEVEL_LINE_SPACING
                if (end == endSum) end -= LEVEL_LINE_SPACING
                for (i in start..end step LEVEL_LINE_SPACING) {
                    ReferenceLine {
                        val x = min(i - domainY.first, domainX.last)
                        val y = min(i - domainX.first, domainY.last)
                        segment = [SegmentMarker(x, i - x), SegmentMarker(i - y, y)]
                        stroke = "#888"
                        ifOverflow = ReferenceLineOverflow.hidden
                        Label {
                            value = i.toString()
                            position = ComponentPosition.insideBottomRight
                            offset = 25
                            style = unsafeJso {
                                fill = TITLE_FONT_COLOR.lighten(1.5)
                            }
                        }
                    }
                }
                if (instances.isNotEmpty()) {
                    ReferenceLine {
                        val min = min(domainX.first, domainY.first)
                        val max = max(domainX.last, domainY.last)
                        strokeDasharray = "10 10"
                        stroke = "#666"
                        segment = [SegmentMarker(min, min), SegmentMarker(max, max)]
                        ifOverflow = ReferenceLineOverflow.hidden
                    }
                }
                Scatter {
                    data = instances
                    fill = TITLE_FONT_COLOR.toString()
                    isAnimationActive = false
                    LabelList {
                        position = ComponentPosition.top
                        dataKey = { instance: Triple<T, Double, Double> -> instance.first }
                        formatter = { instance: T -> if (shouldShowLabel[instance] == true) props.pointLabel(instance) else "" }
                        style = unsafeJso {
                            this["fill"] = "white"
                        }
                    }
                    onClick = { event ->
                        if (!touch) props.onClick(event["payload"].unsafeCast<Triple<T, Double, Double>>().first)
                    }
                }
                Tooltip {
                    content = ComparisonScatterTooltip
                }
            }
        }

        var searchRef = useRef<HTMLInputElement>()
        useEffectOnce {
            val type = EventType<Event>("keydown")
            val handler: EventHandler<Event, Window, EventTarget> =
                EventHandler { event: KeyboardEvent ->
                    if ("/" == event.key && searchRef.current != document.activeElement) {
                        event.preventDefault()
                        searchRef.current?.focus()
                    }
                }

            window.addEventListener(type, handler)
            awaitCancellation { window.removeEventListener(type, handler) }
        }

        input {
            ref = searchRef
            css {
                position = Position.absolute
                bottom = 0.px
                left = 0.px
                margin = 0.5.em
                padding = 0.5.em
                width = 5.em
                background = TITLE_FONT_COLOR
                color = BACKGROUND_COLOR
                border = None.none
                borderRadius = 0.25.em
                outline = None.none

                placeholder {
                    color = BACKGROUND_COLOR
                }
            }
            value = lookup?.toString() ?: ""
            onChange = { event ->
                val s = event.currentTarget.value.take(5)
                val i = s.toIntOrNull()
                if (s.isEmpty() || null != i) lookup = i
            }
            onKeyDown = { event ->
                if (event.key == "Enter") {
                    event.preventDefault()
                    instances.firstOrNull { props.lookup(it.first) == lookup }?.let { props.onClick(it.first) }
                }
            }
            placeholder = "Search"
        }
    }
}

external interface ComparisonScatterPlotProps<T> : Props {
    var data: List<T>
    var sumMetricTitle: String?
    var xMetricTitle: String
    var xMetric: (T) -> Double
    var yMetricTitle: String
    var yMetric: (T) -> Double
    var pointLabel: (T) -> String
    var tooltipLabel: (T) -> String
    var lookup: (T) -> Int?
    var onlyShowTopLabels: Boolean
    var onClick: (T) -> Unit
    var onToggle: () -> Unit
}

private fun domain(range: ClosedFloatingPointRange<Double>?): IntRange {
    val min = (range?.start?.minus(TICK_SPACING / 2) ?: 0).floorTo(TICK_SPACING)
    val max = (range?.endInclusive?.plus(TICK_SPACING / 2) ?: TICK_SPACING).ceilTo(TICK_SPACING)
    return min..max
}

fun Number.floorTo(multiple: Int) = multiple * floor(this.toDouble() / multiple).roundToInt()
fun Number.ceilTo(multiple: Int) = multiple * ceil(this.toDouble() / multiple).roundToInt()
